import {cloneElement, FunctionComponent, PropsWithChildren, ReactElement, ReactNode, useMemo} from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {Box, Breakpoint, Drawer, paperClasses, Theme, useMediaQuery} from "@mui/material";
import UIUtils from "../../../../../../../utils";


export interface DashboardLayoutProps {
    mobileThreshold: Breakpoint,
    alwaysMobile?: boolean;
    sidebar?: ReactNode;
    drawerOpen?: boolean;
    sidebarOpen?: boolean;
    closeDrawer?: () => void;
}

const DashboardViewLayout: FunctionComponent<PropsWithChildren<DashboardLayoutProps>> = ({
                                                                                             mobileThreshold,
                                                                                             children,
                                                                                             sidebar,
                                                                                             sidebarOpen,
                                                                                             drawerOpen,
                                                                                             closeDrawer,
                                                                                             alwaysMobile,
                                                                                         }) => {
    const shouldUseDrawer = useMediaQuery<Theme>(theme => theme.breakpoints.down(alwaysMobile ? Number.MAX_SAFE_INTEGER : mobileThreshold));
    const isSidebarOpen = useMemo(() => sidebarOpen && !shouldUseDrawer, [shouldUseDrawer, sidebarOpen]);

    const SidebarComponent = useMemo(() => {
        if (UIUtils.isObjectValidElementType(sidebar)) {
            const sidebarComponent = sidebar as ReactElement;
            return cloneElement(sidebarComponent, {
                ...sidebarComponent.props,
                onClose: shouldUseDrawer ? closeDrawer : null,
            }, ...sidebarComponent.props.children);
        }
        return sidebar;
    }, [closeDrawer, shouldUseDrawer, sidebar])

    return (
        <>
            <Box sx={{position: 'relative'}}>
                <Grid
                    spacing={2}
                    container
                    sx={(theme) => ({
                        width: 'calc(100%)',
                        position: 'relative',
                        padding: 2,
                        marginBlock: '0 !important',
                        marginInline: '0 !important',
                        paddingRight: !shouldUseDrawer && isSidebarOpen ? 0 : 2,
                        transition: {...(!alwaysMobile && {[mobileThreshold]: theme.transitions.create(['padding-right'])})},
                    })}
                >
                    <Grid
                        xs={12}
                        {...{[mobileThreshold]: isSidebarOpen ? true : 12}}
                        sx={(theme) => ({
                            height: 'calc(100%)',
                            transition: {...(!alwaysMobile && {[mobileThreshold]: theme.transitions.create(['width', 'max-width'])})},
                        })}
                    >
                        {children}
                    </Grid>
                    {
                        !shouldUseDrawer &&
                        <Grid
                            xs={0}
                            {...{[mobileThreshold]: isSidebarOpen ? 'auto' : 0}}
                            sx={(theme) => ({
                                backgroundColor: 'background.paper',
                                height: 'calc(100%)',
                                position: 'sticky',
                                borderRadius: 1,
                                padding: 0,
                                top: 0,
                                maxWidth: {md: 500},
                                width: {md: 'min(500px, 100vw)'},
                                transition: {...(!alwaysMobile && {[mobileThreshold]: theme.transitions.create(['width', 'padding'])})},
                            })}
                        >
                            {
                                sidebarOpen &&
                                SidebarComponent
                            }
                        </Grid>
                    }
                    {
                        shouldUseDrawer &&
                        <Drawer
                            anchor="right"
                            open={drawerOpen}
                            onClose={closeDrawer}
                            variant={'temporary'}
                            sx={{
                                [`.${paperClasses.root}`]: {
                                    width: {md: 'min(500px, 100vw)'},
                                    maxWidth: {md: 500},
                                }
                            }}
                        >
                            {SidebarComponent}
                        </Drawer>
                    }
                </Grid>
            </Box>
        </>
    );
}

export default DashboardViewLayout;